import type {
  CalculateTravelTimesResponse,
  ChatHistoryResponse,
  ChatResponse,
  CreatePropertyRequest,
  CreatePropertyResponse,
  FlatSearch,
  ListPropertiesResponse,
  Location,
  MemberPreferences,
  Property,
  SiteRequestResponse,
  TravelTimeDestination,
  UpdatePropertyRequest,
  UpdatePropertyResponse,
} from '../types/api';
import type { UserProfileResponse } from '../types/tier';
import { getValidToken, clearTokens } from './auth';

export class ApiError extends Error {
  public readonly code?: string;
  public readonly tier?: string;
  public readonly limit?: number;
  public readonly current?: number;

  constructor(
    public readonly status: number,
    message: string,
    errorBody?: { code?: string; tier?: string; limit?: number; current?: number },
  ) {
    super(message);
    this.name = 'ApiError';
    this.code = errorBody?.code;
    this.tier = errorBody?.tier;
    this.limit = errorBody?.limit;
    this.current = errorBody?.current;
    Object.setPrototypeOf(this, ApiError.prototype);
  }
}

class ApiService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = import.meta.env.VITE_API_URL || '';
  }

  private async request<T>(path: string, options: RequestInit = {}): Promise<T> {
    const token = await getValidToken();
    if (!token) {
      throw new Error('Not authenticated');
    }

    const response = await fetch(`${this.baseUrl}${path}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        ...options.headers,
      },
    });

    if (response.status === 401) {
      clearTokens();
      throw new ApiError(401, 'Session expired');
    }

    if (!response.ok) {
      let errorBody: { error?: string; code?: string; tier?: string; limit?: number; current?: number } | undefined;
      try {
        errorBody = await response.json();
      } catch {
        // Response may not be JSON
      }
      throw new ApiError(
        response.status,
        errorBody?.error ?? `API error: ${response.status}`,
        errorBody?.code ? errorBody : undefined,
      );
    }

    return response.json();
  }

  async getUserProfile(): Promise<UserProfileResponse> {
    return this.request<UserProfileResponse>('/profile');
  }

  async listSearches(): Promise<FlatSearch[]> {
    return this.request<FlatSearch[]>('/searches');
  }

  async listProperties(searchId: string): Promise<ListPropertiesResponse> {
    const params = new URLSearchParams({ searchId });
    return this.request<ListPropertiesResponse>(`/properties?${params}`);
  }

  async createProperty(data: CreatePropertyRequest): Promise<CreatePropertyResponse> {
    return this.request<CreatePropertyResponse>('/properties', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getProperty(searchId: string, id: string): Promise<Property> {
    return this.request<Property>(`/properties/${id}?searchId=${searchId}`);
  }

  async updateProperty(searchId: string, id: string, data: UpdatePropertyRequest): Promise<UpdatePropertyResponse> {
    return this.request<UpdatePropertyResponse>(`/properties/${id}?searchId=${searchId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteProperty(searchId: string, id: string): Promise<void> {
    const token = await getValidToken();
    if (!token) {
      throw new Error('Not authenticated');
    }

    const response = await fetch(`${this.baseUrl}/properties/${id}?searchId=${searchId}`, {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (response.status === 401) {
      clearTokens();
      throw new ApiError(401, 'Session expired');
    }

    if (!response.ok) {
      throw new ApiError(response.status, `API error: ${response.status}`);
    }
  }

  async createInvite(searchId: string): Promise<{ inviteCode: string }> {
    return this.request<{ inviteCode: string }>(`/searches/${searchId}/invite`, {
      method: 'POST',
    });
  }

  async removeMember(searchId: string, userId: string): Promise<FlatSearch> {
    return this.request<FlatSearch>(`/searches/${searchId}/members/${userId}`, {
      method: 'DELETE',
    });
  }

  async updateSearch(searchId: string, data: { name?: string; status?: 'completed' | 'archived'; rentedPropertyId?: string }): Promise<FlatSearch> {
    return this.request<FlatSearch>(`/searches/${searchId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async createSearch(name: string): Promise<FlatSearch> {
    return this.request<FlatSearch>('/searches', {
      method: 'POST',
      body: JSON.stringify({ name }),
    });
  }

  async updateLocations(searchId: string, locations: { name: string; address: string; isPrimary?: boolean }[]): Promise<void> {
    await this.request('/locations', {
      method: 'PUT',
      body: JSON.stringify({ searchId, locations }),
    });
  }

  async sendInviteEmail(searchId: string, email: string): Promise<void> {
    await this.request(`/searches/${searchId}/invite/send`, {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }

  async joinSearch(inviteCode: string): Promise<FlatSearch> {
    return this.request<FlatSearch>('/searches/join', {
      method: 'POST',
      body: JSON.stringify({ inviteCode }),
    });
  }

  async createPresignedUrl(propertyId: string, contentType: string): Promise<{ uploadUrl: string; url: string }> {
    return this.request<{ uploadUrl: string; url: string }>('/uploads/presigned', {
      method: 'POST',
      body: JSON.stringify({ propertyId, contentType }),
    });
  }

  async getLocations(searchId: string): Promise<Location[]> {
    const response = await this.request<{ locations: Location[] }>(`/locations?searchId=${searchId}`);
    return response.locations;
  }

  async requestSite(url: string): Promise<SiteRequestResponse> {
    return this.request<SiteRequestResponse>('/site-requests', {
      method: 'POST',
      body: JSON.stringify({ url }),
    });
  }

  async calculateTravelTimes(origin: string, destinations: TravelTimeDestination[]): Promise<CalculateTravelTimesResponse> {
    return this.request<CalculateTravelTimesResponse>('/travel-times', {
      method: 'POST',
      body: JSON.stringify({ origin, destinations }),
    });
  }

  // =========================================================================
  // Chat (AI Conversational Assistant)
  // =========================================================================

  async sendChatMessage(searchId: string, message: string): Promise<ChatResponse> {
    return this.request<ChatResponse>(`/searches/${searchId}/chat`, {
      method: 'POST',
      body: JSON.stringify({ message }),
    });
  }

  async getChatHistory(
    searchId: string,
    options?: { limit?: number; cursor?: string },
  ): Promise<ChatHistoryResponse> {
    const params = new URLSearchParams();
    if (options?.limit) params.set('limit', String(options.limit));
    if (options?.cursor) params.set('cursor', options.cursor);
    const qs = params.toString();
    return this.request<ChatHistoryResponse>(`/searches/${searchId}/chat${qs ? `?${qs}` : ''}`);
  }

  // =========================================================================
  // Member Preferences
  // =========================================================================

  async getMemberPreferences(searchId: string, userId?: string): Promise<{ preferences: MemberPreferences | MemberPreferences[] }> {
    const path = userId
      ? `/searches/${searchId}/preferences/${userId}`
      : `/searches/${searchId}/preferences`;
    return this.request<{ preferences: MemberPreferences | MemberPreferences[] }>(path);
  }

  async updateMemberPreferences(
    searchId: string,
    userId: string,
    data: Partial<Pick<MemberPreferences, 'commuteLocations' | 'personalMustHaves' | 'personalDealbreakers' | 'personalNiceToHaves' | 'onboardingCompletedAt' | 'emailNotificationsEnabled'>>,
  ): Promise<{ preferences: MemberPreferences }> {
    return this.request<{ preferences: MemberPreferences }>(`/searches/${searchId}/preferences/${userId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // =========================================================================
  // Billing (Stripe)
  // =========================================================================

  async createCheckoutSession(priceId: string): Promise<{ url: string }> {
    return this.request<{ url: string }>('/billing/checkout', {
      method: 'POST',
      body: JSON.stringify({ priceId }),
    });
  }

  async createPortalSession(): Promise<{ url: string }> {
    return this.request<{ url: string }>('/billing/portal', {
      method: 'POST',
    });
  }

  async submitFeedback(type: 'feature' | 'broken-site' | 'bug', subject: string, body: string): Promise<void> {
    await this.request<{ ok: boolean }>('/feedback', {
      method: 'POST',
      body: JSON.stringify({ type, subject, body }),
    });
  }

  static readonly MAX_IMAGE_SIZE = 25 * 1024 * 1024; // 25MB

  async uploadImage(propertyId: string, file: File): Promise<string> {
    if (file.size > ApiService.MAX_IMAGE_SIZE) {
      throw new Error(`File "${file.name}" exceeds 25MB limit (${(file.size / 1024 / 1024).toFixed(1)}MB)`);
    }

    const { uploadUrl, url } = await this.createPresignedUrl(propertyId, file.type);
    const response = await fetch(uploadUrl, {
      method: 'PUT',
      body: file,
      headers: { 'Content-Type': file.type },
    });

    if (!response.ok) {
      throw new Error(`Failed to upload "${file.name}" (${response.status})`);
    }

    return url;
  }
}

export const api = new ApiService();
